import { ErroApi, api, chaveDeIntencao } from './api';
import { protegerArmazenamento } from './instalar';

/**
 * A fila de envio — o aparelho guarda, a rede leva.
 *
 * ── O que o briefing pede ───────────────────────────────────────────
 *
 * §1: «possibilidade de lançamento off-line dos dados de campo e
 * sincronização posterior». §10: «sincronização off-line». §18:
 * «salvamento automático», «indicador de sincronização», «aviso claro de
 * ausência de conexão».
 *
 * ── Por que ela só pôde nascer depois da 0144 ───────────────────────
 *
 * Uma fila que sincroniza depois é uma fila que REENVIA. Sem a chave da
 * intenção, cada reenvio seria uma duplicata — e a fila transformaria um
 * acidente ocasional (o 4G que engasga) num defeito diário. A chave nasce
 * aqui, com a linha, e acompanha TODA tentativa dela: é o que faz «a
 * resposta se perdeu no caminho» custar zero.
 *
 * ── Por que IndexedDB, e não `localStorage` ─────────────────────────
 *
 * Porque o técnico fecha o aplicativo. `localStorage` também sobrevive,
 * mas é síncrono e tem alguns megabytes — e um dia de leituras com
 * fotografia não cabe. IndexedDB é a única coisa que o navegador oferece
 * com tamanho de disco e escrita assíncrona.
 *
 * ── O limite, declarado ─────────────────────────────────────────────
 *
 * A fila só aceita operação sobre coisa que JÁ EXISTE no servidor.
 *
 * Lançar a leitura de um piezômetro instalado, conferir, corrigir: tudo
 * isso passa. Criar um diário off-line e, na mesma viagem, pendurar os
 * pontos dele NÃO passa — o segundo envio precisaria do `id` que só o
 * primeiro vai receber, e um `id` de mentira que se troca depois é a
 * classe de defeito mais cara que existe num sistema com rastreabilidade.
 *
 * Isso não é esquecimento: é a ordem certa. O caso que o §10 pede
 * primeiro é a CAMPANHA DE LEITURA — vinte instrumentos numa barragem sem
 * sinal —, e nele nada depende de nada. A cadeia com pai e filho off-line
 * pede identificador gerado no aparelho, e isso é decisão de modelo, não
 * de tela.
 */

const BASE = 'labmanager-fila';
const LOJA = 'envios';
const VERSAO = 1;

export interface EnvioNaFila {
  id: string;
  /** A chave da intenção (`0144`). A MESMA em toda tentativa. */
  chave: string;
  caminho: string;
  metodo: 'POST' | 'PUT' | 'PATCH';
  corpo: unknown;
  /** O que mostrar para quem olha a fila: «Leitura de PZ-01A · 12/03». */
  rotulo: string;
  criadaEm: number;
  tentativas: number;
  ultimoErro: string | null;
}

/* ── O banco local ──────────────────────────────────────────────────── */

let promessa: Promise<IDBDatabase> | null = null;

function abrir(): Promise<IDBDatabase> {
  if (promessa) return promessa;
  promessa = new Promise((resolver, recusar) => {
    if (typeof indexedDB === 'undefined') {
      recusar(new Error('sem IndexedDB'));
      return;
    }
    const p = indexedDB.open(BASE, VERSAO);
    p.onupgradeneeded = () => {
      const b = p.result;
      if (!b.objectStoreNames.contains(LOJA)) {
        const loja = b.createObjectStore(LOJA, { keyPath: 'id' });
        /* A ordem de chegada é a ordem de saída — ver `drenar`. */
        loja.createIndex('criadaEm', 'criadaEm');
      }
    };
    p.onsuccess = () => resolver(p.result);
    p.onerror = () => recusar(p.error ?? new Error('IndexedDB indisponível'));
  });
  return promessa;
}

function transacao<T>(
  modo: IDBTransactionMode,
  fn: (loja: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return abrir().then((b) => new Promise<T>((resolver, recusar) => {
    const t = b.transaction(LOJA, modo);
    const r = fn(t.objectStore(LOJA));
    r.onsuccess = () => resolver(r.result);
    r.onerror = () => recusar(r.error ?? new Error('falha no banco local'));
  }));
}

/* ── Quem quer saber do tamanho da fila ─────────────────────────────── */

const ouvintes = new Set<(n: number) => void>();
let tamanho = 0;

export const tamanhoDaFila = () => tamanho;

export function aoMudarFila(f: (n: number) => void): () => void {
  ouvintes.add(f);
  return () => ouvintes.delete(f);
}

async function anunciar() {
  try {
    tamanho = await transacao<number>('readonly', (l) => l.count());
  } catch { tamanho = 0; }
  ouvintes.forEach((f) => f(tamanho));
}

/** Chame uma vez ao abrir a aplicação: a fila sobrevive ao aplicativo. */
export async function acordarFila() {
  await anunciar();
  if (tamanho > 0) void drenar();
}

/* ── Enfileirar e enviar ────────────────────────────────────────────── */

/**
 * Marca de que a coisa ficou GUARDADA, e não enviada.
 *
 * É um erro, e não um sucesso silencioso, porque a tela precisa dizer a
 * verdade: «gravado no aparelho, sobe quando houver rede» é uma frase
 * diferente de «gravado». Tratar as duas como iguais é como o técnico
 * volta do campo achando que mandou.
 */
export class FicouNaFila extends Error {
  readonly envio: EnvioNaFila;
  constructor(envio: EnvioNaFila) {
    super('Gravado neste aparelho. Sobe assim que houver rede.');
    this.name = 'FicouNaFila';
    this.envio = envio;
  }
}

export async function guardarEEnviar<T>(
  caminho: string,
  corpo: unknown,
  o: { metodo?: 'POST' | 'PUT' | 'PATCH'; rotulo: string },
): Promise<T> {
  const envio: EnvioNaFila = {
    id: chaveDeIntencao(),
    chave: chaveDeIntencao(),
    caminho,
    metodo: o.metodo ?? 'POST',
    corpo,
    rotulo: o.rotulo,
    criadaEm: Date.now(),
    tentativas: 0,
    ultimoErro: null,
  };

  /*
   * Grava ANTES de tentar. É a diferença entre «o aparelho desligou no
   * meio do envio e o lançamento sumiu» e «o aparelho desligou e o
   * lançamento está lá». O custo é uma escrita local; o benefício é o
   * dado que ninguém precisa relançar.
   */
  let guardou = true;
  try {
    await transacao('readwrite', (l) => l.put(envio));
    await anunciar();
  } catch { guardou = false; }

  try {
    const r = await api<T>(caminho, {
      metodo: envio.metodo, corpo, intencao: envio.chave, tentativas: 0,
    });
    if (guardou) { await remover(envio.id); }
    return r;
  } catch (e) {
    /*
     * SÓ a falha de rede fica na fila.
     *
     * Um 400 é o servidor dizendo que o conteúdo está errado, e reenviar
     * o mesmo conteúdo mil vezes não o conserta — só entope a fila com
     * uma linha que nunca sai. Erro de conteúdo volta para a tela, onde
     * o técnico pode corrigir.
     */
    const rede = e instanceof ErroApi && e.status === 0;
    if (!rede || !guardou) {
      if (guardou) await remover(envio.id);
      throw e;
    }
    /*
     * O lançamento passou a morar SÓ no aparelho: é a hora de pedir ao
     * navegador que não apague o armazenamento quando faltar espaço (onda
     * 298 — o app no celular). No aplicativo instalado o Chrome dá sem
     * perguntar; numa aba comum ele pode não dar, e o quadro «Este
     * aparelho» da página `/instalar` mostra qual dos dois.
     */
    void protegerArmazenamento();
    throw new FicouNaFila(envio);
  }
}

async function remover(id: string) {
  try {
    await transacao('readwrite', (l) => l.delete(id));
    await anunciar();
  } catch { /* a fila é conforto, não correção */ }
}

export async function verFila(): Promise<EnvioNaFila[]> {
  try {
    const tudo = await transacao<EnvioNaFila[]>('readonly', (l) => l.getAll());
    return tudo.sort((a, b) => a.criadaEm - b.criadaEm);
  } catch { return []; }
}

export async function descartarDaFila(id: string) { await remover(id); }

/* ── Drenar ─────────────────────────────────────────────────────────── */

let drenando = false;

/**
 * Esvaziar a fila, em ORDEM DE CHEGADA e uma de cada vez.
 *
 * A ordem importa mesmo sem dependência entre os envios: duas correções
 * da mesma leitura têm de chegar na ordem em que o técnico as fez, senão
 * a segunda é sobrescrita pela primeira. Uma de cada vez porque vinte
 * envios simultâneos num 3G de barragem é como nenhum deles termina.
 *
 * Uma linha que o servidor RECUSA (400, 403, 409) sai da fila e vira
 * pendência à vista: insistir nela travaria todas as que vêm depois, que
 * é o pior desfecho possível — o técnico perde o dia inteiro por causa de
 * um campo.
 */
export async function drenar(): Promise<{ enviados: number; presos: number }> {
  if (drenando) return { enviados: 0, presos: 0 };
  drenando = true;
  let enviados = 0;
  let presos = 0;
  try {
    for (const envio of await verFila()) {
      try {
        await api(envio.caminho, {
          metodo: envio.metodo, corpo: envio.corpo,
          intencao: envio.chave, tentativas: 0,
        });
        await remover(envio.id);
        enviados += 1;
      } catch (e) {
        if (e instanceof ErroApi && e.status === 0) {
          /* Rede caiu de novo: para aqui e guarda o resto para depois. */
          try {
            await transacao('readwrite', (l) => l.put({
              ...envio,
              tentativas: envio.tentativas + 1,
              ultimoErro: null,
            }));
          } catch { /* nada */ }
          break;
        }
        presos += 1;
        try {
          await transacao('readwrite', (l) => l.put({
            ...envio,
            tentativas: envio.tentativas + 1,
            ultimoErro: e instanceof Error ? e.message : String(e),
          }));
        } catch { /* nada */ }
      }
    }
  } finally {
    drenando = false;
    await anunciar();
  }
  return { enviados, presos };
}

try {
  /* A rede voltou: o aparelho tenta sozinho, sem ninguém tocar em nada. */
  addEventListener('online', () => { void drenar(); });
} catch { /* fora do navegador */ }
